import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { supabase, isSupabaseConfigured } from 'lib/supabase';

// ---------------------------------------------------------------------------
// StudentTeamsContext
//
// Powers the "Team on this student" section of the profile page and the
// same section on the printable one-pager. Backed by
// public.student_team_members (see migration 0010).
//
// Live mode:
//   * Reads all rows on mount and subscribes to postgres_changes so
//     "Aisha (OT) just joined Marcus's team" shows up on Sarah's screen
//     without a refresh.
//   * Writes go through Supabase with `default auth.uid()` stamping
//     `added_by` at the DB level.
//
// Demo mode:
//   * A localStorage-backed store with the same shape as the live-mode
//     rows, so demo-only walkthroughs still see the Team section
//     populate and update.
// ---------------------------------------------------------------------------

const TABLE = 'student_team_members';
const STORAGE_KEY = 'edutracker.student_team_members.v1';

const StudentTeamsContext = createContext(null);

function normalizeIncoming(row) {
  if (!row) return null;
  return {
    id:          row.id,
    studentId:   row.student_id ?? row.studentId ?? '',
    userId:      row.user_id    ?? row.userId    ?? '',
    roleOnTeam:  row.role_on_team ?? row.roleOnTeam ?? '',
    addedBy:     row.added_by ?? row.addedBy ?? null,
    addedAt:     row.added_at ?? row.addedAt ?? null,
  };
}

function readLocal() {
  if (typeof window === 'undefined') return [];
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function writeLocal(rows) {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(rows));
  } catch {
    // ignore — in-memory state still works for the session
  }
}

function generateLocalId() {
  return `stm-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

export function StudentTeamsProvider({ children }) {
  const supabaseLive = isSupabaseConfigured() && supabase !== null;
  const [members, setMembers] = useState(() =>
    supabaseLive ? [] : readLocal().map(normalizeIncoming).filter(Boolean),
  );
  const [loading, setLoading] = useState(supabaseLive);
  const [error, setError]     = useState(null);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);

  const refresh = useCallback(async () => {
    if (!supabaseLive) {
      setMembers(readLocal().map(normalizeIncoming).filter(Boolean));
      return;
    }
    setLoading(true);
    setError(null);
    const { data, error: fetchError } = await supabase
      .from(TABLE)
      .select('*')
      .order('added_at', { ascending: false });
    if (!mounted.current) return;
    if (fetchError) {
      setError(fetchError.message || 'Failed to load student teams');
      setLoading(false);
      return;
    }
    setMembers((data || []).map(normalizeIncoming).filter(Boolean));
    setLoading(false);
  }, [supabaseLive]);

  useEffect(() => {
    if (!supabaseLive) return undefined;
    refresh();
    const channel = supabase
      .channel('public:student_team_members')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: TABLE },
        () => refresh(),
      )
      .subscribe();
    return () => {
      supabase.removeChannel(channel);
    };
  }, [supabaseLive, refresh]);

  // Demo-mode cross-tab sync.
  useEffect(() => {
    if (supabaseLive) return undefined;
    const onStorage = (event) => {
      if (event.key === STORAGE_KEY) refresh();
    };
    if (typeof window !== 'undefined') {
      window.addEventListener('storage', onStorage);
      return () => window.removeEventListener('storage', onStorage);
    }
    return undefined;
  }, [supabaseLive, refresh]);

  // -------------------------------------------------------------------------
  // Lookups
  // -------------------------------------------------------------------------

  const byStudent = useMemo(() => {
    const map = new Map();
    for (const m of members) {
      if (!m?.studentId) continue;
      const list = map.get(m.studentId) || [];
      list.push(m);
      map.set(m.studentId, list);
    }
    return map;
  }, [members]);

  const byUser = useMemo(() => {
    const map = new Map();
    for (const m of members) {
      if (!m?.userId) continue;
      const list = map.get(m.userId) || [];
      list.push(m);
      map.set(m.userId, list);
    }
    return map;
  }, [members]);

  const teamForStudent = useCallback(
    (studentId) => (studentId ? byStudent.get(studentId) || [] : []),
    [byStudent],
  );

  const studentsForUser = useCallback(
    (userId) => (userId ? byUser.get(userId) || [] : []),
    [byUser],
  );

  const isOnTeam = useCallback(
    (studentId, userId) => {
      if (!studentId || !userId) return false;
      const list = byStudent.get(studentId) || [];
      return list.some((m) => m.userId === userId);
    },
    [byStudent],
  );

  // -------------------------------------------------------------------------
  // Mutations
  // -------------------------------------------------------------------------

  const addMember = useCallback(
    async ({ studentId, userId, roleOnTeam }) => {
      if (!studentId || !userId) {
        throw new Error('addMember: studentId and userId are required');
      }
      if (isOnTeam(studentId, userId)) {
        // Idempotent — return the existing row instead of throwing on the
        // unique constraint. Lets the "Join this student's team" button
        // stay clickable without extra state juggling.
        return byStudent.get(studentId).find((m) => m.userId === userId);
      }

      const nowIso = new Date().toISOString();

      if (!supabaseLive) {
        const draft = normalizeIncoming({
          id: generateLocalId(),
          student_id: studentId,
          user_id: userId,
          role_on_team: roleOnTeam || null,
          added_by: null,
          added_at: nowIso,
        });
        const next = [draft, ...readLocal()];
        writeLocal(next);
        setMembers((prev) => [draft, ...prev]);
        return draft;
      }

      const { data, error: insertError } = await supabase
        .from(TABLE)
        .insert({
          student_id: studentId,
          user_id: userId,
          role_on_team: roleOnTeam || null,
        })
        .select('*')
        .single();
      if (insertError) {
        setError(insertError.message || 'Could not add teammate to student');
        throw insertError;
      }
      const saved = normalizeIncoming(data);
      setMembers((prev) => [saved, ...prev.filter((m) => m.id !== saved.id)]);
      return saved;
    },
    [supabaseLive, isOnTeam, byStudent],
  );

  const removeMember = useCallback(
    async ({ studentId, userId }) => {
      if (!studentId || !userId) return false;

      if (!supabaseLive) {
        const current = readLocal();
        const next = current.filter(
          (m) =>
            !(
              (m.student_id ?? m.studentId) === studentId &&
              (m.user_id    ?? m.userId)    === userId
            ),
        );
        writeLocal(next);
        setMembers((prev) =>
          prev.filter((m) => !(m.studentId === studentId && m.userId === userId)),
        );
        return true;
      }

      const { error: deleteError } = await supabase
        .from(TABLE)
        .delete()
        .eq('student_id', studentId)
        .eq('user_id', userId);
      if (deleteError) {
        setError(deleteError.message || 'Could not remove teammate');
        throw deleteError;
      }
      setMembers((prev) =>
        prev.filter((m) => !(m.studentId === studentId && m.userId === userId)),
      );
      return true;
    },
    [supabaseLive],
  );

  const updateMember = useCallback(
    async ({ id, roleOnTeam }) => {
      if (!id) return null;

      if (!supabaseLive) {
        const current = readLocal();
        const next = current.map((m) =>
          m.id === id ? { ...m, role_on_team: roleOnTeam || null } : m,
        );
        writeLocal(next);
        setMembers((prev) =>
          prev.map((m) => (m.id === id ? { ...m, roleOnTeam: roleOnTeam || '' } : m)),
        );
        return next.find((m) => m.id === id) || null;
      }

      const { data, error: updateError } = await supabase
        .from(TABLE)
        .update({ role_on_team: roleOnTeam || null })
        .eq('id', id)
        .select('*')
        .single();
      if (updateError) throw updateError;
      const saved = normalizeIncoming(data);
      setMembers((prev) => prev.map((m) => (m.id === id ? saved : m)));
      return saved;
    },
    [supabaseLive],
  );

  const value = useMemo(
    () => ({
      members,
      loading,
      error,
      isLive: supabaseLive,
      refresh,
      teamForStudent,
      studentsForUser,
      isOnTeam,
      addMember,
      removeMember,
      updateMember,
    }),
    [
      members,
      loading,
      error,
      supabaseLive,
      refresh,
      teamForStudent,
      studentsForUser,
      isOnTeam,
      addMember,
      removeMember,
      updateMember,
    ],
  );

  return (
    <StudentTeamsContext.Provider value={value}>
      {children}
    </StudentTeamsContext.Provider>
  );
}

export function useStudentTeams() {
  const ctx = useContext(StudentTeamsContext);
  if (!ctx) {
    throw new Error('useStudentTeams must be used within a <StudentTeamsProvider>');
  }
  return ctx;
}
